/** POS room and store-edge mesh events — 1.6.0 and 1.7.0 additions. */
import {
  asEventRecord,
  defineEvent,
  requireString,
  type EventDefinition,
  type EventSchema,
} from './registry';

export interface PosRoomsEventPayload extends Record<string, unknown> {
  orgId: string;
}

const eventSchema = (
  type: string,
  requiredFields: readonly string[],
): EventSchema<PosRoomsEventPayload> => ({
  parse(data) {
    const record = asEventRecord(data, type);
    for (const field of requiredFields) {
      requireString(record[field], field, type);
    }
    return { ...record, orgId: requireString(record.orgId, 'orgId', type) };
  },
});

const definePosRoomsEvent = (
  type: string,
  description: string,
  requiredFields: readonly string[],
  auditable = true,
  since = '1.6.0',
): EventDefinition<PosRoomsEventPayload> =>
  defineEvent<PosRoomsEventPayload>({
    type,
    version: 1,
    schema: eventSchema(type, ['orgId', ...requiredFields]),
    auditable,
    description,
    since,
  });

export const POS_ROOMS_ROOM_CREATED_EVENT = definePosRoomsEvent(
  'pos-rooms:room-created',
  'A store-edge room was created for an organization.',
  ['roomId'],
);

export const POS_ROOMS_ROOM_UPDATED_EVENT = definePosRoomsEvent(
  'pos-rooms:room-updated',
  'A store-edge room configuration was updated.',
  ['roomId'],
);

export const POS_ROOMS_NODE_REGISTERED_EVENT = definePosRoomsEvent(
  'pos-rooms:node-registered',
  'An edge node was registered to a store room.',
  ['roomId', 'nodeId'],
);

export const POS_ROOMS_NODE_PROMOTED_EVENT = definePosRoomsEvent(
  'pos-rooms:node-promoted',
  'An edge node was promoted to room master.',
  ['roomId', 'nodeId'],
);

export const POS_ROOMS_NODE_DEMOTED_EVENT = definePosRoomsEvent(
  'pos-rooms:node-demoted',
  'An edge node was demoted from room master.',
  ['roomId', 'nodeId'],
);

export const POS_ROOMS_NODE_DISABLED_EVENT = definePosRoomsEvent(
  'pos-rooms:node-disabled',
  'An edge node was disabled by the control plane.',
  ['roomId', 'nodeId'],
);

export const POS_ROOMS_NODE_ENABLED_EVENT = definePosRoomsEvent(
  'pos-rooms:node-enabled',
  'An edge node was enabled by the control plane.',
  ['roomId', 'nodeId'],
);

export const POS_ROOMS_COMMANDS_ISSUED_EVENT = definePosRoomsEvent(
  'pos-rooms:commands-issued',
  'One or more control commands were issued to a store-edge room.',
  ['roomId'],
);

export const POS_ROOMS_INGEST_ANOMALY_EVENT = definePosRoomsEvent(
  'pos-rooms:ingest-anomaly',
  'An edge ingest command requires anomaly review.',
  ['roomId', 'nodeId'],
);

export const POS_ROOMS_REVIEW_ITEM_CREATED_EVENT = definePosRoomsEvent(
  'pos-rooms:review-item-created',
  'A store-edge review item was created for staff follow-up.',
  ['reviewItemId'],
);

export const POS_ROOMS_REVIEW_ITEM_UPDATED_EVENT = definePosRoomsEvent(
  'pos-rooms:review-item-updated',
  'A store-edge review item was resolved or dismissed.',
  ['reviewItemId', 'status'],
  true,
  '1.7.0',
);

// The plan names this cross-cutting mesh event `edge:mesh-degraded`; its
// ClusterEvent source remains `pos-rooms`, so source-based room routing applies.
export const EDGE_MESH_DEGRADED_EVENT = definePosRoomsEvent(
  'edge:mesh-degraded',
  'A store-edge mesh degraded below its expected operating capability.',
  ['roomId'],
);

export const POS_ROOMS_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [POS_ROOMS_ROOM_CREATED_EVENT.type]: POS_ROOMS_ROOM_CREATED_EVENT,
  [POS_ROOMS_ROOM_UPDATED_EVENT.type]: POS_ROOMS_ROOM_UPDATED_EVENT,
  [POS_ROOMS_NODE_REGISTERED_EVENT.type]: POS_ROOMS_NODE_REGISTERED_EVENT,
  [POS_ROOMS_NODE_PROMOTED_EVENT.type]: POS_ROOMS_NODE_PROMOTED_EVENT,
  [POS_ROOMS_NODE_DEMOTED_EVENT.type]: POS_ROOMS_NODE_DEMOTED_EVENT,
  [POS_ROOMS_NODE_DISABLED_EVENT.type]: POS_ROOMS_NODE_DISABLED_EVENT,
  [POS_ROOMS_NODE_ENABLED_EVENT.type]: POS_ROOMS_NODE_ENABLED_EVENT,
  [POS_ROOMS_COMMANDS_ISSUED_EVENT.type]: POS_ROOMS_COMMANDS_ISSUED_EVENT,
  [POS_ROOMS_INGEST_ANOMALY_EVENT.type]: POS_ROOMS_INGEST_ANOMALY_EVENT,
  [POS_ROOMS_REVIEW_ITEM_CREATED_EVENT.type]: POS_ROOMS_REVIEW_ITEM_CREATED_EVENT,
  [POS_ROOMS_REVIEW_ITEM_UPDATED_EVENT.type]: POS_ROOMS_REVIEW_ITEM_UPDATED_EVENT,
  [EDGE_MESH_DEGRADED_EVENT.type]: EDGE_MESH_DEGRADED_EVENT,
};
